import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TableSortLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FieldsPicker from '../chatbot/FieldsPicker';
import ColumnVisibilityPicker from '../chatbot/ColumnVisibilityPicker';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentFailureReportTitle, selectReportsResults, selectCurrentFailureReportTitle, 
  setCurrentFailureReportSubtitle, selectNlgParameterTransmissionResult,
  selectIsLoading, selectReportFilePath } from '../../store/features/transmissionSlice';
import { nlgParameterTransmission } from '../../store/services/transmissionService';

type Order = 'asc' | 'desc';

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any) => string;
  numeric?: boolean;
  isDate?: boolean;
}

interface FailureReportTableProps {
  columns: Column[];
  data: any[];
  title?: string;
  visibleColumns?: string[];
  subtitle?: string;
  onVisibleColumnsChange?: (visibleColumns: string[]) => void;
}

const FailureReportTable: React.FC<FailureReportTableProps> = ({
  columns,
  data,
  title,
  subtitle,
  visibleColumns: propVisibleColumns,
  onVisibleColumnsChange
}) => {
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('');
  const [displayType, setDisplayType] = useState<'table' | 'chart'>('table');
  const [axes, setAxes] = useState({ x: '', y: '' });
  const [openFieldPicker, setOpenFieldPicker] = useState(false);
  const [openColumnPicker, setOpenColumnPicker] = useState(false);
  const [internalVisibleColumns, setInternalVisibleColumns] = useState<string[]>(columns.map(col => col.id));
  const [columnPickerAnchorEl, setColumnPickerAnchorEl] = useState<HTMLElement | null>(null);
  const reportResults = useSelector(selectReportsResults);
  const currentFailureReportTitle = useSelector(selectCurrentFailureReportTitle);
  const reportFilePath = useSelector(selectReportFilePath);
  const [selectedRowTimestamp, setSelectedRowTimestamp] = useState<string>('');

  // Use prop visible columns if provided, otherwise use internal state
  const visibleColumns = propVisibleColumns || internalVisibleColumns;

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleShowPicker = () => {
    if (displayType === 'table') {
      setOpenFieldPicker(true);
    } else {
      setOpenFieldPicker(false);
      setDisplayType('table');
    }
  };

  const handleSetAxes = (newAxes: { x: string; y: string }) => {
    setAxes(newAxes);
  };

  const handleSetChart = (type: 'chart' | 'table') => {
    setDisplayType(type);
  };

  const handleColumnVisibilityChange = (newVisibleColumns: string[]) => {
    if (onVisibleColumnsChange) {
      onVisibleColumnsChange(newVisibleColumns);
    } else {
      setInternalVisibleColumns(newVisibleColumns);
    }
  };

  const handleRowClick = async (rowData: any[], rowIndex: number) => {
    setSelectedRowTimestamp(reportResults.fail_report[rowIndex + 1][15]);
    dispatch(setCurrentFailureReportTitle(reportResults.fail_report[rowIndex + 1][5]));
    dispatch(setCurrentFailureReportSubtitle(reportResults.fail_report[rowIndex + 1][6]));
    await nlgParameterTransmission({
      failed_parameter: reportResults.fail_report[rowIndex + 1][6],
      test_report: reportFilePath,
      failed_test: reportResults.fail_report[rowIndex + 1][5],
      value: reportResults.fail_report[rowIndex + 1][10],
      minimum: reportResults.fail_report[rowIndex + 1][11],
      maximum: reportResults.fail_report[rowIndex + 1][12]
    }, state, dispatch);
  };

  const isRowSelected = (rowIndex: number) => {
    return selectedRowTimestamp === reportResults.fail_report[rowIndex + 1][15];
  };

  const filteredColumns = columns.filter(col => visibleColumns.includes(col.id));

  // Create filtered data that matches the filtered columns
  const filteredData = React.useMemo(() => {
    return data.map(row => {
      const filteredRow: any[] = [];
      filteredColumns.forEach(col => {
        const columnIndex = columns.findIndex(c => c.id === col.id);
        filteredRow.push(row[columnIndex]);
      });
      return filteredRow;
    });
  }, [data, filteredColumns, columns]);

  // Update internal visible columns when columns prop changes
  useEffect(() => {
    setInternalVisibleColumns(columns.map(col => col.id));
  }, [columns]);

  const isValidDate = (str: string) => {
    const date = new Date(str);
    return !isNaN(date.getTime());
  }

  const formatDate = (value: any): string => {
    if (!value) return '';
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    } catch {
      return value;
    }
  };

  const sortedData = React.useMemo(() => {
    if (!orderBy) return filteredData;

    const column = filteredColumns.find(col => col.id === orderBy);
    if (!column) return filteredData;

    return [...filteredData].sort((a, b) => {
      const columnIndex = filteredColumns.findIndex(col => col.id === orderBy);
      const aValue = a[columnIndex];
      const bValue = b[columnIndex];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (aValue === bValue) return 0;

      let comparison = 0;

      if (!isNaN(aValue)) {
        // Numeric comparison
        comparison = Number(aValue) - Number(bValue);
      } else if (isValidDate(aValue)) {
        // Date comparison
        const dateA = new Date(aValue).getTime();
        const dateB = new Date(bValue).getTime();
        comparison = dateA - dateB;
      } else {
        // String comparison
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, order, orderBy, filteredColumns]);

  const row_data = sortedData.map(row => ({
    x: row[filteredColumns.findIndex(col => col.id === axes.x)],
    y: row[filteredColumns.findIndex(col => col.id === axes.y)]
  }));

  return (
    <Box sx={{ width: '100%', mb: 1, overflowY: 'auto', height: '40vh' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 1, 
        backgroundColor: '#156082', 
        color: '#ffffff',
        py: 0.5,
        px: 1
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
          <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
            Serial number: {title}
          </Typography>
          <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', textAlign: 'left', lineHeight: 1.2 }}>
            Model: {subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {displayType === 'table' && (
            <Tooltip title="Column Visibility">
              <IconButton
                size="small"
                onClick={(event) => {
                  setColumnPickerAnchorEl(event.currentTarget);
                  setOpenColumnPicker(true);
                }}
              >
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              {filteredColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sx={{
                    minWidth: column.minWidth,
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    py: 0.5,
                    px: 1,
                    fontSize: '0.9rem'
                  }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={() => handleRequestSort(column.id)}
                    sx={{ fontSize: '0.9rem' }}
                  >
                    {column.label}
                    {orderBy === column.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, rowIndex) => (
              <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={rowIndex}
                onClick={() => handleRowClick(row, rowIndex)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: isRowSelected(rowIndex) ? '#aac9e8' : 'inherit',
                  '&:hover': {
                    backgroundColor: isRowSelected(rowIndex) ? '#aac9e8' : '#f5f5f5'
                  }
                }}
              >
                {filteredColumns.map((column, index) => {
                  const value = row[index];
                  let formattedValue = value;

                  return (
                    <TableCell 
                      key={column.id} 
                      align={column.align || 'left'}
                      sx={{
                        py: 0.25,
                        px: 1,
                        fontSize: '0.9rem',
                        lineHeight: 1.2
                      }}
                    >
                      {formattedValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <FieldsPicker
        open={openFieldPicker}
        setOpen={setOpenFieldPicker}
        fields={filteredColumns.map(col => col.label)}
        setAxes={handleSetAxes}
        setChart={handleSetChart}
      />

      <ColumnVisibilityPicker
        open={openColumnPicker}
        setOpen={setOpenColumnPicker}
        columns={columns}
        visibleColumns={visibleColumns}
        onVisibilityChange={handleColumnVisibilityChange}
        anchorEl={columnPickerAnchorEl}
      />
    </Box>
  );
};

export default FailureReportTable; 